/**
 * Calculations list — /settings/calculations
 *
 * Lists every saved calculation in the tenant. Click to edit, or
 * "New calculation" to create one. Each row shows the key, name,
 * description, and optional module scoping.
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { createFileRoute, Link } from '@tanstack/react-router'
import { Calculator, Plus, Trash2 } from 'lucide-react'
import { toast } from 'sonner'
import { Button } from '@/components/ui/button'
import { Card, CardContent } from '@/components/ui/card'
import { Skeleton } from '@/components/ui/skeleton'
import { orpc } from '@/lib/orpcClient'

export const Route = createFileRoute('/settings/calculations/')({
  component: CalculationsListPage,
})

function CalculationsListPage() {
  const queryClient = useQueryClient()

  const { data: calculations, isLoading } = useQuery(
    orpc.extensions.calculations.list.queryOptions({
      input: {},
    }),
  )

  const deleteMutation = useMutation(
    orpc.extensions.calculations.delete.mutationOptions({
      onSuccess: () => {
        toast.success('Calculation deleted')
        void queryClient.invalidateQueries({
          queryKey: orpc.extensions.calculations.list.queryKey({ input: {} }),
        })
      },
      onError: (err) => toast.error(err.message),
    }),
  )

  return (
    <div className="container mx-auto py-6 px-4 max-w-4xl space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-semibold flex items-center gap-2">
            <Calculator className="h-5 w-5" />
            Calculations
          </h2>
          <p className="text-sm text-muted-foreground">
            Typed, unit-aware formulas. Callable from workflows, embedded in layouts,
            or run standalone from this page.
          </p>
        </div>
        <Link to="/settings/calculations/new">
          <Button>
            <Plus className="h-4 w-4 mr-2" />
            New calculation
          </Button>
        </Link>
      </div>

      {isLoading ? (
        <Skeleton className="h-40 w-full" />
      ) : !calculations || calculations.length === 0 ? (
        <Card>
          <CardContent className="py-12 text-center">
            <Calculator className="h-10 w-10 text-muted-foreground mx-auto mb-3" />
            <p className="text-sm text-muted-foreground">
              No calculations yet. Create one to use from workflows or layout widgets.
            </p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-2">
          {calculations.map((c) => (
            <Card key={c.id}>
              <CardContent className="flex items-center justify-between gap-4 py-4">
                <div className="min-w-0 flex-1">
                  <Link
                    to="/settings/calculations/$key"
                    params={{ key: c.key }}
                    className="hover:underline"
                  >
                    <div className="font-medium">{c.name}</div>
                  </Link>
                  <div className="text-xs text-muted-foreground font-mono">{c.key}</div>
                  {c.description && (
                    <p className="text-sm text-muted-foreground mt-1 line-clamp-2">
                      {c.description}
                    </p>
                  )}
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  <Link
                    to="/settings/calculations/$key/run"
                    params={{ key: c.key }}
                  >
                    <Button variant="outline" size="sm">
                      Run
                    </Button>
                  </Link>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => {
                      if (confirm(`Delete calculation "${c.name}"?`)) {
                        deleteMutation.mutate({ key: c.key })
                      }
                    }}
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  )
}
